import { confirmAgeReq } from '@/features/age-verification/api';
import {
  AGE_CONFIRMATION_COOKIE_MAX_AGE,
  AGE_CONFIRMATION_COOKIE_NAME,
  AGE_CONFIRMATION_COOKIE_VALUE,
} from '@/features/age-verification/consts';

/**
 * Check if user has confirmed they are 18+ from userData
 */
export const hasAgeConfirmation = (userData: { isAdult?: boolean } | null): boolean => {
  return userData?.isAdult === true;
};

/**
 * Read guest age confirmation from document.cookie (client-only)
 */
export const hasGuestAgeConfirmationCookie = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  try {
    const cookieEntries = document.cookie.split('; ');
    const ageCookie = cookieEntries.find((entry) =>
      entry.startsWith(`${AGE_CONFIRMATION_COOKIE_NAME}=`),
    );

    if (!ageCookie) {
      return false;
    }

    return (
      ageCookie.slice(AGE_CONFIRMATION_COOKIE_NAME.length + 1) === AGE_CONFIRMATION_COOKIE_VALUE
    );
  } catch {
    return false;
  }
};

/**
 * Persist guest age confirmation in a long-lived cookie (client-only)
 */
export const setGuestAgeConfirmationCookie = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${AGE_CONFIRMATION_COOKIE_NAME}=${AGE_CONFIRMATION_COOKIE_VALUE}; path=/; max-age=${AGE_CONFIRMATION_COOKIE_MAX_AGE}; SameSite=Lax${secureFlag}`;
  } catch {
    // Silently fail if cookies are not available
  }
};

/**
 * Save age confirmation: API for authenticated users, cookie for guests
 */
export const saveAgeConfirmation = async (isAuthenticated: boolean): Promise<boolean> => {
  if (!isAuthenticated) {
    setGuestAgeConfirmationCookie();
    return true;
  }

  const result = await confirmAgeReq();
  return result.success;
};

/**
 * Get user age status based on session and userData
 */
export const getUserAgeStatus = (
  isAuthenticated: boolean,
  userData: { isAdult?: boolean } | null,
): 'unauthenticated' | 'authenticated-unconfirmed' | 'authenticated-confirmed' => {
  if (!isAuthenticated) {
    return 'unauthenticated';
  }

  return hasAgeConfirmation(userData) ? 'authenticated-confirmed' : 'authenticated-unconfirmed';
};
